// client/src/components/pages/Settings/LexiStarAssets/OwnershipTransferDialog.jsx
//
// Record a change of owning organisation for an asset (D26, T1-S08). The server
// closes the current ownership period and opens the new one at the same
// instant (lexistar.transfer_asset_ownership, migration 019).
//
// Effective from: optional single date (react-datepicker, the single-date picker
// the WeighbridgeCompliance modals use), sent as toApiDateString() 'YYYY-MM-DD'
// = midnight SAST. Empty or today = now. The server does not accept future
// dates yet, nor a date before the current owner's period started.
//
// The organisation list comes from the existing tenants API, which only global
// admins may read; anyone else sees a notice instead of a picker.

import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../OperationOverview/datepicker-custom.css';
import { FaTimes, FaExclamationTriangle, FaExchangeAlt } from 'react-icons/fa';
import ThemedSelect from '../../../common/ThemedSelect';
import { toApiDateString, formatAppDate } from '../../../../utils/dateUtils';
import {
  listTransferTenants,
  getOwnershipHistory,
  transferOwnership,
} from '../../../../services/lexistarAssets';

const inputClasses =
  'w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm';

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const OwnershipTransferDialog = ({ isOpen, asset, onClose, onTransferred }) => {
  const [tenants, setTenants] = useState([]);
  const [tenantsError, setTenantsError] = useState(null);
  const [current, setCurrent] = useState(null);
  const [toTenantId, setToTenantId] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !asset) return undefined;
    let alive = true;
    setToTenantId('');
    setEffectiveDate(null);
    setError(null);
    setTenantsError(null);
    setCurrent(null);
    setLoading(true);

    Promise.allSettled([listTransferTenants(), getOwnershipHistory(asset.id)]).then(
      ([tenantsRes, historyRes]) => {
        if (!alive) return;
        if (tenantsRes.status === 'fulfilled') {
          setTenants(tenantsRes.value || []);
        } else {
          setTenants([]);
          setTenantsError(
            tenantsRes.reason?.response?.status === 403
              ? 'Only a global admin can see the list of organisations. Ask one to record this transfer.'
              : 'Could not load organisations.'
          );
        }
        if (historyRes.status === 'fulfilled') {
          const rows = historyRes.value?.ownership || [];
          setCurrent(rows.find((r) => r.is_current) || null);
        }
        setLoading(false);
      }
    );
    return () => {
      alive = false;
    };
  }, [isOpen, asset]);

  if (!isOpen || !asset) return null;

  const options = tenants.filter((t) => t.tenant_id !== asset.tenant_id);
  const target = options.find((t) => String(t.tenant_id) === String(toTenantId));
  const today = startOfDay(new Date());
  const minDate = current?.effective_from ? new Date(current.effective_from) : undefined;

  const handleSubmit = async () => {
    if (!toTenantId) {
      setError('Choose the organisation that now owns this asset');
      return;
    }
    // Today (or nothing) = now: midnight could predate an ownership that started today.
    const effectiveFrom =
      effectiveDate && startOfDay(effectiveDate).getTime() < today.getTime()
        ? toApiDateString(effectiveDate)
        : undefined;
    setSaving(true);
    setError(null);
    try {
      const result = await transferOwnership({
        assetId: asset.id,
        toTenantId: parseInt(toTenantId, 10),
        effectiveFrom,
      });
      onTransferred?.(result);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Transfer failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Transfer ${asset.registration}`}
        className="bg-surface rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-visible"
        style={{ animation: 'fadeInScale 0.2s ease-out' }}
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">Transfer ownership</h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <FaTimes size={18} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Asset</label>
            <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900">
              <span className="font-mono font-medium">{asset.registration}</span>
              <span className="text-slate-500">
                {' '}
                — owned by {asset.tenant_name || `organisation ${asset.tenant_id}`}
                {current?.effective_from ? ` since ${formatAppDate(current.effective_from)}` : ''}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">New owner</label>
            {tenantsError ? (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                <FaExclamationTriangle className="text-amber-500 mt-0.5 flex-shrink-0" size={14} />
                <p className="text-sm text-amber-800">{tenantsError}</p>
              </div>
            ) : (
              <ThemedSelect
                value={toTenantId}
                onChange={(e) => setToTenantId(e.target.value)}
                className={inputClasses}
                disabled={loading}
                aria-label="New owner"
              >
                <option value="">{loading ? 'Loading organisations...' : 'Choose...'}</option>
                {options.map((t) => (
                  <option key={t.tenant_id} value={t.tenant_id}>
                    {t.tenant_name} ({t.tenant_code})
                  </option>
                ))}
              </ThemedSelect>
            )}
          </div>

          <div>
            <label
              htmlFor="lexistar-transfer-effective-from"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              Effective from
            </label>
            <DatePicker
              id="lexistar-transfer-effective-from"
              selected={effectiveDate}
              onChange={(d) => setEffectiveDate(d)}
              maxDate={new Date()}
              minDate={minDate}
              dateFormat="yyyy/MM/dd"
              placeholderText="Now"
              isClearable
              className={inputClasses}
              wrapperClassName="w-full"
            />
            <p className="mt-1 text-xs text-slate-500">
              Leave empty, or pick today, to transfer now. An earlier date records when the sale
              happened.
            </p>
          </div>

          {target && (
            <p className="text-sm text-slate-600 leading-relaxed">
              <span className="font-mono font-medium text-slate-900">{asset.registration}</span>{' '}
              will belong to <strong>{target.tenant_name}</strong> from{' '}
              {effectiveDate && startOfDay(effectiveDate).getTime() < today.getTime()
                ? formatAppDate(effectiveDate)
                : 'now'}
              . After that only {target.tenant_name} and global admins see it.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !!tenantsError || loading}
            className={`inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white rounded-lg transition-all shadow-sm hover:shadow ${saving || tenantsError || loading ? 'bg-slate-400 cursor-not-allowed' : 'bg-primary-500 hover:bg-primary-600'}`}
          >
            <FaExchangeAlt size={12} />
            {saving ? 'Transferring...' : 'Transfer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OwnershipTransferDialog;
